import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { StageBackdrop } from "@/components/landing/StageBackdrop";
import { LanguageSelector } from "@/components/hud/LanguageSelector";
import { Loader2, LogIn, UserPlus } from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { Card } from "@/components/primitives/Card";
import { Input } from "@/components/primitives/Input";
import { Toaster } from "@/components/ui/sonner";
import { login, register, listProviders, LEGAL_ACCEPT_KEY } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { tr } from "@/i18n";
import { translateError } from "@/lib/errors";
import { LOGIN, REGISTER } from "@/constants/testIds";

function apiError(detail: any): string {
  if (!detail) return tr("errors.generic");
  if (typeof detail === "string") return translateError(detail);
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function Login() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptLegal, setAcceptLegal] = useState(false); // mandatory, never pre-checked (18+, Terms, Privacy)
  const [providers, setProviders] = useState<string[]>([]);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    listProviders()
      .then((d) => setProviders(d.enabled))
      .catch(() => setProviders(["password"]));
  }, []);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const data =
        mode === "login"
          ? await login(form.email.trim(), form.password)
          : await register(form.email.trim(), form.password, form.name.trim() || undefined, acceptLegal);
      setUser(data.user);
      navigate("/dashboard", { replace: true });
    } catch (e: any) {
      setError(apiError(e?.response?.data?.detail) || tr("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    if (!acceptLegal) {
      setError(t("legal.acceptRequired"));
      return;
    }
    sessionStorage.setItem(LEGAL_ACCEPT_KEY, "1");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const legalCheckbox = (
    <label data-testid="legal-accept-label" className="flex items-start gap-3 rounded-md border border-border/70 bg-black/20 px-3 py-2.5 text-xs leading-relaxed text-muted-foreground">
      <input
        type="checkbox"
        data-testid="legal-accept-checkbox"
        checked={acceptLegal}
        onChange={(e) => setAcceptLegal(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 accent-[#7FD4FF]"
      />
      <span>
        {t("legal.acceptPrefix")}{" "}
        <Link to="/terms" className="text-[#7FD4FF] underline-offset-4 hover:underline" data-testid="legal-accept-terms-link">{t("legal.terms")}</Link>{" "}
        {t("legal.acceptAnd")}{" "}
        <Link to="/privacy" className="text-[#7FD4FF] underline-offset-4 hover:underline" data-testid="legal-accept-privacy-link">{t("legal.privacy")}</Link>.
      </span>
    </label>
  );

  return (
    <div className="relative isolate min-h-screen bg-background text-foreground">
      <StageBackdrop intensity="full" />
      <div className="absolute right-4 top-4 z-20 sm:right-6 sm:top-6" data-testid="login-language">
        <LanguageSelector />
      </div>
      <main
        data-testid="login-page"
        className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-16"
      >
        <Link to="/" className="mb-8 flex flex-col items-center gap-3" data-testid="login-logo">
          <img
            src="/brand/logo-simulation-studios.jpeg"
            alt="Simulation Studios"
            className="logo-neon h-16 w-16 object-contain"
          />
          <span className="font-display text-sm font-semibold uppercase tracking-[0.22em] text-[#2E7BFF]">
            Simulation Studios
          </span>
        </Link>

        <Card>
          <div className="mb-6 flex overflow-hidden rounded-md border border-border">
            {(["login", "register"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m);
                  setError(null);
                }}
                data-testid={m === "login" ? "auth-tab-login" : "auth-tab-register"}
                className={
                  "flex-1 px-3 py-2 font-display text-xs font-bold uppercase tracking-widest transition-colors " +
                  (mode === m
                    ? "bg-[#2E7BFF] text-[#05070D]"
                    : "bg-transparent text-muted-foreground hover:text-foreground")
                }
              >
                {m === "login" ? t("common.login") : t("common.register")}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <Input
                label={t("login.nameLabel")}
                name="name"
                data-testid={REGISTER.nameInput}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("login.namePlaceholder")}
              />
            )}
            <Input
              label={t("common.email")}
              name="email"
              type="email"
              autoComplete="email"
              data-testid={mode === "login" ? LOGIN.emailInput : REGISTER.emailInput}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={t("login.emailPlaceholder")}
            />
            <Input
              label={t("common.password")}
              name="password"
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              data-testid={mode === "login" ? LOGIN.passwordInput : REGISTER.passwordInput}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={mode === "login" ? t("login.passwordPlaceholder") : t("login.passwordHint")}
            />

            {mode === "register" && legalCheckbox}

            {error && (
              <p
                data-testid="auth-error"
                className="rounded-md border border-[#FF4D4D]/40 bg-[#FF4D4D]/10 px-3 py-2 text-sm text-[#FF8A8A]"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={busy}
              className="w-full justify-center"
              data-testid={mode === "login" ? LOGIN.submitButton : REGISTER.submitButton}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? <LogIn className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
              {mode === "login" ? t("common.login") : t("login.createAccount")}
            </Button>
          </form>

          {providers.includes("google") && (
            <>
              <div className="my-6 flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                  {t("common.or")}
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>
              {mode === "login" && <div className="mb-4">{legalCheckbox}</div>}
              <Button
                type="button"
                variant="secondary"
                onClick={googleLogin}
                className="w-full justify-center"
                data-testid="auth-google-btn"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                  <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.4a5.5 5.5 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.6-5.2 3.6-8.8Z" />
                  <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2a7 7 0 0 1-6.6-4.8H1.4v3.1A12 12 0 0 0 12 24Z" />
                  <path fill="#FBBC05" d="M5.4 14.5a7.2 7.2 0 0 1 0-4.6V6.8H1.4a12 12 0 0 0 0 10.8l4-3.1Z" />
                  <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A11.5 11.5 0 0 0 12 0 12 12 0 0 0 1.4 6.8l4 3.1A7 7 0 0 1 12 4.8Z" />
                </svg>
                {t("login.google")}
              </Button>
            </>
          )}
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          {t("login.footer")}
        </p>
      </main>
      <Toaster position="top-right" theme="dark" richColors />
    </div>
  );
}
